<template>
  <div class="mt-4 border-t border-border pt-4 space-y-3">
    <!-- Essential (always on, disabled) -->
    <div class="flex items-start justify-between gap-4 rounded-lg p-3 bg-muted/50">
      <div class="flex-1">
        <p class="text-sm font-medium">{{ $ts('consent.essential.label') }}</p>
        <p class="mt-0.5 text-xs text-muted-foreground">
          {{ $ts('consent.essential.description') }}
        </p>
      </div>
      <button
        type="button"
        disabled
        class="relative inline-flex h-5 w-9 shrink-0 cursor-not-allowed items-center rounded-full bg-primary opacity-70"
        :aria-label="$ts('consent.essential.label')"
      >
        <span class="pointer-events-none inline-block h-4 w-4 translate-x-4 rounded-full bg-white shadow-sm transition-transform" />
      </button>
    </div>

    <!-- Analytics -->
    <div class="flex items-start justify-between gap-4 rounded-lg p-3 hover:bg-muted/30 transition-colors">
      <div class="flex-1">
        <p class="text-sm font-medium">{{ $ts('consent.analytics.label') }}</p>
        <p class="mt-0.5 text-xs text-muted-foreground">
          {{ $ts('consent.analytics.description') }}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        :aria-checked="localPrefs.analytics"
        :aria-label="$ts('consent.analytics.label')"
        :class="[
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors',
          localPrefs.analytics ? 'bg-primary' : 'bg-input',
        ]"
        @click="localPrefs.analytics = !localPrefs.analytics"
      >
        <span
          :class="[
            'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
            localPrefs.analytics ? 'translate-x-4' : 'translate-x-0.5',
          ]"
        />
      </button>
    </div>

    <!-- Marketing -->
    <div class="flex items-start justify-between gap-4 rounded-lg p-3 hover:bg-muted/30 transition-colors">
      <div class="flex-1">
        <p class="text-sm font-medium">{{ $ts('consent.marketing.label') }}</p>
        <p class="mt-0.5 text-xs text-muted-foreground">
          {{ $ts('consent.marketing.description') }}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        :aria-checked="localPrefs.marketing"
        :aria-label="$ts('consent.marketing.label')"
        :class="[
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors',
          localPrefs.marketing ? 'bg-primary' : 'bg-input',
        ]"
        @click="localPrefs.marketing = !localPrefs.marketing"
      >
        <span
          :class="[
            'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
            localPrefs.marketing ? 'translate-x-4' : 'translate-x-0.5',
          ]"
        />
      </button>
    </div>

    <!-- Third-party -->
    <div class="flex items-start justify-between gap-4 rounded-lg p-3 hover:bg-muted/30 transition-colors">
      <div class="flex-1">
        <p class="text-sm font-medium">{{ $ts('consent.thirdParty.label') }}</p>
        <p class="mt-0.5 text-xs text-muted-foreground">
          {{ $ts('consent.thirdParty.description') }}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        :aria-checked="localPrefs.third_party"
        :aria-label="$ts('consent.thirdParty.label')"
        :class="[
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors',
          localPrefs.third_party ? 'bg-primary' : 'bg-input',
        ]"
        @click="localPrefs.third_party = !localPrefs.third_party"
      >
        <span
          :class="[
            'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
            localPrefs.third_party ? 'translate-x-4' : 'translate-x-0.5',
          ]"
        />
      </button>
    </div>

    <!-- Save button -->
    <div class="flex justify-end pt-2">
      <Button variant="default" size="sm" @click="handleSave">
        {{ $t('consent.savePreferences') }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue';
import { useI18n } from '#imports';
import Button from '#bubba/shared/components/ui/button/Button.vue';
import type { ConsentPreferences } from '#bubba/shared/composables/consent/useConsentState';

const { $t, $ts } = useI18n();

const props = defineProps<{
  preferences: ConsentPreferences;
}>();

const emit = defineEmits<{
  save: [prefs: ConsentPreferences];
}>();

const localPrefs = reactive<ConsentPreferences>({
  analytics: props.preferences.analytics,
  marketing: props.preferences.marketing,
  third_party: props.preferences.third_party,
});

function handleSave() {
  emit('save', { ...localPrefs });
}
</script>
